import { useState, useEffect } from "react";

const navItems = [
  { idx: "01", label: "Expertise", href: "#expertise", id: "expertise" },
  { idx: "02", label: "Experience", href: "#experience", id: "experience" },
  { idx: "03", label: "Projects", href: "#projects", id: "projects" },
  { idx: "04", label: "Contact", href: "#contact", id: "contact" },
];

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem("theme") === "dark";
  });
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    const root = window.document.documentElement;
    if (darkMode) {
      root.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      root.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [darkMode]);

  useEffect(() => {
    const options = { root: null, rootMargin: "0px", threshold: 0.6 };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActiveSection(entry.target.id);
      });
    }, options);

    ["home", ...navItems.map((n) => n.id)].forEach((id) => {
      const section = document.getElementById(id);
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const SunIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2v2m6.364 1.636l-1.414 1.414M20 12h-2M17.364 17.364l-1.414-1.414M12 20v-2M6.636 17.364l1.414-1.414M4 12H2m3.636-6.364l1.414 1.414M12 6a6 6 0 100 12 6 6 0 000-12z" />
    </svg>
  );

  const MoonIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path d="M17.293 13.293a8 8 0 01-10.586-10.586A8 8 0 1017.293 13.293z" />
    </svg>
  );

  return (
    <nav className="fixed w-full z-50 bg-light/80 dark:bg-dark/80 backdrop-blur-md text-dark dark:text-light border-b border-black/10 dark:border-white/10 transition-colors">
      <div className="max-w-7xl mx-auto px-6 h-[68px] flex items-center justify-between">
        {/* Brand */}
        <a href="#home" className="text-lg font-bold tracking-tight">
          James Clyne<span className="text-accent">.</span>
        </a>

        {/* Desktop nav links */}
        <div className="hidden md:flex items-center gap-9">
          {navItems.map(({ idx, label, href, id }) => (
            <a
              key={href}
              href={href}
              className={`font-mono text-[13px] transition-colors ${
                activeSection === id
                  ? "text-dark dark:text-light"
                  : "text-muted hover:text-dark dark:hover:text-light"
              }`}
            >
              <span className="text-accent mr-1.5">{idx}</span>
              {label}
            </a>
          ))}
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="p-2 border border-black/15 dark:border-white/15 rounded-md hover:border-accent hover:text-accent transition"
            aria-label="Toggle dark mode"
          >
            {darkMode ? SunIcon : MoonIcon}
          </button>
        </div>

        {/* Mobile controls */}
        <div className="flex md:hidden items-center gap-3">
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="p-2 border border-black/15 dark:border-white/15 rounded-md hover:border-accent hover:text-accent transition"
            aria-label="Toggle dark mode"
          >
            {darkMode ? SunIcon : MoonIcon}
          </button>
          <button onClick={() => setMenuOpen(!menuOpen)} className="focus:outline-none" aria-label="Toggle menu">
            <div className={`flex flex-col justify-center items-center transform transition-transform duration-300 ${menuOpen ? "rotate-90" : ""}`}>
              <div className="w-6 h-0.5 bg-current mb-1" />
              <div className="w-6 h-0.5 bg-current mb-1" />
              <div className="w-6 h-0.5 bg-current" />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-light dark:bg-dark text-dark dark:text-light px-6 pb-4 border-t border-black/10 dark:border-white/10">
          {navItems.map(({ idx, label, href, id }) => (
            <a
              key={href}
              href={href}
              className={`block py-2 font-mono text-sm ${
                activeSection === id ? "text-accent" : "hover:text-accent"
              } transition`}
              onClick={() => setMenuOpen(false)}
            >
              <span className="text-accent mr-2">{idx}</span>
              {label}
            </a>
          ))}
        </div>
      )}
    </nav>
  );
}
